import type { CompressedTexture, Object3D } from 'three'
import { Box3, Group, type Material, Mesh, Source, Texture } from 'three'
import { enumerateMaterialTextures } from '@/plugins/mesh/MeshResourceTracker'
import type {
  TileContentPackage,
  TileDecodeContext,
  TileResourceCosts,
  TileTextureQualityUpdate,
} from '@/plugins/tiles/contracts'
import type { GltfContentDecoderOptions } from '@/plugins/tiles/mesh/GltfContentDecoder'
import { createGltfDependencyReader } from '@/plugins/tiles/mesh/gltfDependencyReader'
import { readGltfManifest, readImageInfo } from '@/plugins/tiles/mesh/gltfManifest'
import { MeshContentResources } from '@/plugins/tiles/mesh/meshContentResources'
import { createBufferViewResolver } from '@/plugins/tiles/mesh/meshoptViews'
import { getTextureDimensions } from '@/plugins/tiles/mesh/textureSizing'
import { throwIfTilesAborted } from '@/plugins/tiles/TilesError'

interface QualitySource {
  readonly owner: MeshContentResources
  readonly imageInfo: readonly ReturnType<typeof readImageInfo>[]
  readonly textureSources: readonly number[]
  readonly inputBytes: number
  readonly setQuality: (size: number) => void
}

/** 只重建图片与材质；GLTFLoader、Draco 和几何拓扑不会在质量切换中重新执行。 */
export class TileTextureQualityUpdater {
  private readonly _sources = new WeakMap<TileContentPackage, QualitySource>()

  constructor(
    private readonly _options: GltfContentDecoderOptions,
    private readonly _decodeImage: (
      bytes: ArrayBuffer,
      info: ReturnType<typeof readImageInfo>,
      target: { width: number; height: number },
      context: TileDecodeContext
    ) => Promise<TexImageSource>
  ) {}

  register(content: TileContentPackage, source: QualitySource): void {
    this._sources.set(content, source)
  }

  estimate(content: TileContentPackage, maxTextureSize: number): TileResourceCosts {
    const source = this._sources.get(content)
    if (!source) throw new Error('内容不支持图片质量更新')
    const targets = source.imageInfo.map((info) =>
      getTextureDimensions(info, { maxTextureSize, textureScale: this._options.textureScale ?? 1 })
    )
    const cpuTextureBytes = targets.reduce((sum, image) => sum + image.width * image.height * 4, 0)
    const gpuTextureBytes = source.textureSources.reduce(
      (sum, index) => sum + Math.ceil((targets[index].width * targets[index].height * 16) / 3),
      0
    )
    const transientBytes =
      source.inputBytes * 2 +
      source.imageInfo.reduce((sum, info) => sum + info.width * info.height * 8, 0) +
      cpuTextureBytes * 2
    return {
      inputBytes: source.inputBytes,
      geometryBytes: 0,
      textureBytes: cpuTextureBytes + gpuTextureBytes,
      cpuTextureBytes,
      gpuTextureBytes,
      transientBytes,
      memory: { cpuBytes: transientBytes, gpuBytes: gpuTextureBytes },
    }
  }

  async update(
    content: TileContentPackage,
    maxTextureSize: number,
    context: TileDecodeContext
  ): Promise<TileTextureQualityUpdate> {
    const source = this._sources.get(content)
    if (!source) throw new Error('内容不支持图片质量更新')
    const owner = new MeshContentResources()
    let views: ReturnType<typeof createBufferViewResolver> | undefined
    let committed = false
    let disposed = false
    const abort = () => owner.disposeSafely()
    context.signal.addEventListener('abort', abort, { once: true })
    try {
      context.reserveCosts?.(this.estimate(content, maxTextureSize))
      const data = await context.read(content.source.sourceUrl, {
        signal: context.signal,
        maxBytes: source.inputBytes,
      })
      throwIfTilesAborted(context.signal)
      const manifest = readGltfManifest(
        data,
        !!this._options.decodeKtx2,
        !!this._options.decodeMeshopt,
        this._options.optionalMetadataPolicy,
        !!this._options.draco
      )
      const { json } = manifest
      if ((json.images?.length ?? 0) !== source.imageInfo.length)
        throw new Error('图片源版本已变化，请重新加载模型')
      const dependencies = createGltfDependencyReader(data, context, source.inputBytes)
      const buffers = await dependencies.readBuffers(manifest)
      views = createBufferViewResolver(json, buffers, context.signal, this._options.decodeMeshopt)
      const imageBytes = await Promise.all(
        (json.images ?? []).map((image: any) =>
          image.uri !== undefined ? dependencies.read(image.uri) : views!.resolve(image.bufferView)
        )
      )
      const images = new Map<number, Promise<TexImageSource | CompressedTexture>>()
      const load = (index: number) => {
        let pending = images.get(index)
        if (!pending) {
          const info = readImageInfo(imageBytes[index])
          const expected = source.imageInfo[index]
          if (
            info.width !== expected.width ||
            info.height !== expected.height ||
            info.mimeType !== expected.mimeType
          )
            throw new Error('图片源尺寸或格式已变化，请重新加载模型')
          const target = getTextureDimensions(info, {
            maxTextureSize,
            textureScale: this._options.textureScale ?? 1,
          })
          pending =
            info.mimeType === 'image/ktx2'
              ? this._options.decodeKtx2!(imageBytes[index], context.signal, target).then(
                  (texture) => {
                    owner.track(texture)
                    if (texture.image.width > target.width || texture.image.height > target.height)
                      throw new Error('KTX2 超出质量目标')
                    return texture
                  }
                )
              : this._decodeImage(imageBytes[index], info, target, context).then((image) =>
                  owner.trackImage(image)
                )
          context.trackDecode?.(pending)
          images.set(index, pending)
        }
        return pending
      }
      const replacements = new Map<Texture, Texture>()
      const oldMaterials = new Set<Material>()
      content.object3D.traverse((object) => {
        if (object instanceof Mesh)
          for (const material of Array.isArray(object.material)
            ? object.material
            : [object.material])
            oldMaterials.add(material)
      })
      for (const material of oldMaterials) {
        for (const texture of enumerateMaterialTextures(material)) {
          if (replacements.has(texture)) continue
          const textureIndex = texture.userData.tilesTextureIndex
          if (!Number.isInteger(textureIndex))
            throw new Error('纹理缺少稳定源索引，不能更新图片质量')
          const decoded = await load(source.textureSources[textureIndex])
          throwIfTilesAborted(context.signal)
          const next = texture.clone()
          if (decoded instanceof Texture) {
            const compressed = next as CompressedTexture
            compressed.source = decoded.source
            compressed.mipmaps = decoded.mipmaps
            compressed.format = decoded.format
            compressed.type = decoded.type
            compressed.internalFormat = decoded.internalFormat
            if (compressed.mipmaps.length < 2) compressed.minFilter = decoded.minFilter
          } else next.source = new Source(decoded)
          next.needsUpdate = true
          owner.track(next)
          replacements.set(texture, next)
        }
      }
      // KTX2 解码器返回的临时 Texture 仅提供 mip 数据，从未提交到 renderer。
      // 候选克隆持有同一数据；移除临时对象，避免 GPU 成本重复计入。
      for (const pending of images.values()) {
        const decoded = await pending
        if (decoded instanceof Texture) {
          owner.tracker.textures.delete(decoded)
          decoded.dispose()
        }
      }
      const materials = new Map<Material, Material>()
      const replaceTextures = (value: unknown): unknown => {
        if (value instanceof Texture) return replacements.get(value) ?? value
        if (Array.isArray(value)) return value.map(replaceTextures)
        if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype)
          return Object.fromEntries(
            Object.entries(value).map(([key, item]) => [key, replaceTextures(item)])
          )
        return value
      }
      for (const material of oldMaterials) {
        const next = material.clone()
        for (const key of Object.keys(next))
          (next as unknown as Record<string, unknown>)[key] = replaceTextures(
            (next as unknown as Record<string, unknown>)[key]
          )
        owner.track(next)
        materials.set(material, next)
      }
      const root = content.object3D.clone(true)
      root.traverse((object) => {
        if (object instanceof Mesh)
          object.material = Array.isArray(object.material)
            ? object.material.map((item) => materials.get(item)!)
            : materials.get(object.material)!
      })
      const packageFor = (
        resources: MeshContentResources,
        object3D: Object3D
      ): TileContentPackage => ({
        adapterId: content.adapterId,
        source: content.source,
        object3D,
        boundingBox: new Box3().copy(content.boundingBox),
        get costs() {
          return resources.costs()
        },
        createRetirement: () => resources.createRetirement(),
        dispose: () => resources.dispose(),
      })
      const candidate = packageFor(owner, root)
      return {
        candidate,
        costs: candidate.costs,
        commit: () => {
          if (committed || disposed || source.owner.disposed) throw new Error('图片质量候选已失效')
          committed = true
          const previous = source.owner.swapAppearance(owner)
          content.object3D.traverse((object) => {
            if (object instanceof Mesh)
              object.material = Array.isArray(object.material)
                ? object.material.map((item) => materials.get(item)!)
                : materials.get(object.material)!
          })
          source.setQuality(maxTextureSize)
          return packageFor(previous, new Group())
        },
        dispose: () => {
          if (disposed) return
          disposed = true
          owner.disposeSafely()
          root.clear()
        },
      }
    } catch (error) {
      owner.disposeSafely()
      throw error
    } finally {
      views?.clear()
      context.signal.removeEventListener('abort', abort)
    }
  }
}
